import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { toast } from "../../stores/toast";
import { BankInQrButton } from "./BankInQrButton";

function CopyRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast("success", `${label} copied`);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast("error", "Could not copy — try selecting the text manually");
    }
  }

  return (
    <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-white/40">{label}</p>
        <p className="truncate font-mono text-sm font-semibold text-white">{value}</p>
      </div>
      <button
        type="button"
        onClick={() => void copy()}
        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-white/70 transition-colors hover:border-brand-pink/40 hover:text-brand-pink"
        aria-label={`Copy ${label.toLowerCase()}`}
      >
        {copied ? <Check size={16} className="text-emerald-400" /> : <Copy size={16} />}
      </button>
    </div>
  );
}

export function BankInTransferDetails({
  instructions,
  bank,
  accountNumber,
  accountName,
  qrImageUrl,
  instructionsFallback,
}: {
  instructions?: string | null;
  bank?: string | null;
  accountNumber?: string | null;
  accountName?: string | null;
  qrImageUrl?: string | null;
  instructionsFallback?: string;
}) {
  const hasAccounts = Boolean(bank?.trim() || accountNumber?.trim() || accountName?.trim());
  const instructionText = instructions?.trim() || instructionsFallback;

  return (
    <div className="space-y-2">
      {instructionText && (
        <p className="rounded-xl bg-amber-500/10 px-3 py-2 text-[11px] leading-relaxed whitespace-pre-wrap text-amber-300">
          {instructionText}
        </p>
      )}
      {hasAccounts && (
        <div className="space-y-2">
          {bank?.trim() && <CopyRow label="Bank" value={bank.trim()} />}
          {accountName?.trim() && <CopyRow label="Account name" value={accountName.trim()} />}
          {accountNumber?.trim() && <CopyRow label="Account number" value={accountNumber.trim()} />}
        </div>
      )}
      {qrImageUrl && <BankInQrButton imageUrl={qrImageUrl} />}
    </div>
  );
}
